import React, { useEffect, useState } from 'react';
import { Crown, Loader2, Trash2 } from 'lucide-react';
import { grantIsActive, grantPro, revokeGrant, watchProGrants, type ProGrantRecord } from '../utils/proGrant';
import { useToast } from './ToastProvider';

interface AdminPanelProps {
    /** Whoever runs the site, signed in */
    admin: { id: string; email: string };
}

/** A day as these pages write it: 29 Oct 2026 */
const formatDay = (time: number): string => new Date(time).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const toLocalInput = (time: number | null): string => {
    if (!time) return '';
    const date = new Date(time);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The site's own screen: Pro access given by hand, without payment. One record per address;
 * the app honours it the moment it is written (utils/proGrant.ts).
 */
export const AdminPanel: React.FC<AdminPanelProps> = ({ admin }) => {
    const toast = useToast();
    const [grants, setGrants] = useState<ProGrantRecord[] | null>(null);
    const [listError, setListError] = useState<string | null>(null);
    const [email, setEmail] = useState('');
    const [until, setUntil] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const now = Date.now();

    useEffect(() => watchProGrants((next) => { setGrants(next); setListError(null); }, setListError), []);

    const give = async () => {
        if (busy) return;
        setError(null);
        const expiresAt = until ? new Date(until).getTime() : null;
        if (until && !Number.isFinite(expiresAt)) { setError('That end date cannot be read.'); return; }
        setBusy('give');
        try {
            const record = await grantPro(admin, email, { expiresAt, note });
            toast.success(`Pro access given to ${record.email}`, record.expiresAt ? `Until ${formatDay(record.expiresAt)}.` : 'For as long as the record is there.');
            setEmail('');
            setUntil('');
            setNote('');
        } catch (e: any) {
            setError(e?.message || 'The access could not be given.');
        } finally {
            setBusy(null);
        }
    };

    const revoke = async (record: ProGrantRecord) => {
        if (busy) return;
        setBusy(record.email);
        try {
            await revokeGrant(record.email);
            toast.success(`Pro access taken away from ${record.email}`);
        } catch (e: any) {
            toast.error('Could not take the access away', e?.message || 'Please try again.');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4" data-admin-panel>
            <section className="ns-card">
                <h2 className="text-lg font-semibold text-ink mb-1">Give Pro access</h2>
                <p className="text-sm text-ink-soft mb-4">
                    The person gets the Pro plan without paying, the moment the record is written, for as long as it is there or until the end you set.
                    They need an account with this address.
                </p>
                <form className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_200px]" onSubmit={(e) => { e.preventDefault(); void give(); }}>
                    <div>
                        <label className="ns-field-label" htmlFor="admin-grant-email">Email address</label>
                        <input id="admin-grant-email" type="email" className="ns-field" value={email} placeholder="name@example.com" autoComplete="off" disabled={!!busy}
                            onChange={(e) => { setEmail(e.target.value); setError(null); }} />
                    </div>
                    <div>
                        <label className="ns-field-label" htmlFor="admin-grant-until">Until (optional)</label>
                        <input id="admin-grant-until" type="datetime-local" className="ns-field" value={until} min={toLocalInput(now)} disabled={!!busy} onChange={(e) => setUntil(e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="ns-field-label" htmlFor="admin-grant-note">Note (optional, shown to them on the plan page)</label>
                        <input id="admin-grant-note" type="text" className="ns-field" value={note} maxLength={200} placeholder="Festival jury, 2026" disabled={!!busy} onChange={(e) => setNote(e.target.value)} />
                    </div>
                    {error && <p className="ns-note sm:col-span-2" data-tone="danger" role="alert">{error}</p>}
                    <div className="sm:col-span-2 flex justify-end">
                        <button type="submit" className="ns-btn ns-btn-primary ns-btn-sm" disabled={!!busy || !email.trim()}>
                            {busy === 'give' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Crown className="w-4 h-4" aria-hidden="true" />}
                            Give Pro access
                        </button>
                    </div>
                </form>
            </section>

            <section className="ns-card">
                <h2 className="text-lg font-semibold text-ink mb-4">Access given</h2>
                {listError && <p className="ns-note mb-3" data-tone="danger" role="alert">{listError}</p>}
                {grants === null && !listError && (
                    <div className="flex items-center justify-center py-8 text-ink-faint" aria-label="Loading"><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /></div>
                )}
                {grants && grants.length === 0 && <p className="text-sm text-ink-soft">Nobody has been given access yet.</p>}
                {grants && grants.length > 0 && (
                    <ul className="divide-y divide-cream-200" aria-label="People given Pro access">
                        {grants.map(record => {
                            const active = grantIsActive(record, now);
                            return (
                                <li key={record.email} className="flex items-center gap-3 py-2.5" data-grant={record.email}>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-sm font-medium text-ink truncate">{record.email}</span>
                                            <span className={`ns-chip ns-chip-sm ${active ? 'ns-chip-ok' : ''}`}>{active ? (record.tier === 'enterprise' ? 'Enterprise' : 'Pro') : 'Ended'}</span>
                                        </div>
                                        <div className="ns-t-small truncate">
                                            {record.expiresAt ? `Until ${formatDay(record.expiresAt)}` : 'No end'}
                                            {record.note ? ` · ${record.note}` : ''}
                                            {record.grantedAt ? ` · given ${formatDay(record.grantedAt)}${record.grantedBy ? ` by ${record.grantedBy}` : ''}` : ''}
                                        </div>
                                    </div>
                                    <button type="button" className="ns-btn ns-btn-quiet ns-btn-xs" data-danger="true" disabled={!!busy} onClick={() => void revoke(record)} aria-label={`Take Pro access away from ${record.email}`}>
                                        {busy === record.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                                        Take away
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
};
